/*
 * Dates on the Guest Questionnaires screens, written day-first.
 *
 * QA 2026-10-20: the first client is Royal Irish Tours (Ireland and England),
 * and the head-office dashboard and the guide's Feedback QR page were showing
 * departures in US order ("September 20, 2026") because they formatted with
 * the browser's default locale or react-intl on a bare 'en'. English now reads
 * en-GB ("20 September 2026"); every other language keeps its own locale,
 * which is already day-first for French ("20 septembre 2026"). The guest form
 * in TA-Tipping (containers/QuestionnairePage/flow.js) follows the same rule.
 *
 * Display only: stored values and API formats are untouched.
 */

/** The locale to write dates in for an app language ('en' -> 'en-GB'). */
export const dateLocale = locale => {
  if (!locale) return 'en-GB';
  const lower = String(locale).toLowerCase();
  if (lower === 'en' || lower === 'en-us') return 'en-GB';
  return locale;
};

/**
 * A plain 'YYYY-MM-DD' (a departure) is pinned to noon UTC so it stays on its
 * own day in every time zone; anything else is read as an instant.
 */
const toDate = value =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T12:00:00Z`)
    : new Date(value);

/**
 * Format a date day-first. `options` are Intl.DateTimeFormat options; a plain
 * departure date is always formatted in UTC. Returns '' for a missing or
 * unreadable value rather than "Invalid Date".
 */
export const formatDayFirst = (value, options = {}, locale = 'en') => {
  if (!value) return '';
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return '';
  const plainDay = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
  return new Intl.DateTimeFormat(dateLocale(locale), {
    ...options,
    ...(plainDay ? { timeZone: 'UTC' } : {})
  }).format(date);
};
